/******************************************************************************
 * Auto Diag compliance status — a technician's standing for the current cycle,
 * as returned by GET admin/auto-diag/technicians.
 *
 * That endpoint returns only technicians who are NOT current, so a technician
 * absent from it is up to date.
 *****************************************************************************/

/** Mirrors core enum AutoDiagStatus. */
export type AutoDiagStatusValue = "CURRENT" | "DUE_SOON" | "OVERDUE_BLOCKED";

/**
 * One technician's compliance standing.
 *
 * `nextDue` is the deadline they are working towards — the one they have already missed when it
 * sits in the past.
 */
export interface AutoDiagTechnicianStatus {
  technicianId: string;
  status: AutoDiagStatusValue;
  /** ISO-8601 instant, or null. */
  nextDue: string | null;
  /** True once the technician is barred from starting new work. */
  blocked: boolean;
}

/**
 * isLateForAutoDiag: whether the technician has missed their deadline.
 *
 * Keyed on `nextDue` being in the past rather than on the status enum, because `DUE_SOON` straddles
 * the deadline: it covers both the alert window *before* it and the grace window *after* it. Reading
 * "late" off the enum alone would either warn about technicians who still have days left, or stay
 * silent about ones who are already past due but not yet blocked.
 *
 * @param status - the technician's standing, or undefined when they are current
 * @param now - injectable for tests
 */
export function isLateForAutoDiag(
  status: AutoDiagTechnicianStatus | undefined,
  now: Date = new Date(),
): boolean {
  if (!status) return false;
  if (status.blocked) return true;
  if (!status.nextDue) return false;
  const due = new Date(status.nextDue);
  return !isNaN(due.getTime()) && due.getTime() < now.getTime();
}
/***/
